import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  X,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  ArrowRight,
  Plus,
  FileText,
  ExternalLink,
} from "react-feather";
import { useSidePanel } from "../context/SidePanelContext";
import { useFloatingPanels } from "../context/FloatingPanelContext";
import { getFilesFromStorage } from "../utils/fileStorage";
import Resources from "../views/resources";
import AssignmentAssistant from "./AssignmentAssistant";
import styles from "../styles/sidePanel.module.css";
import { useAssignment } from "../context/AssignmentContext";

const SidePanel = () => {
  const {
    panelFile,
    contentComponent,
    panelTitle,
    isCollapsed,
    position,
    width,
    documentTabs,
    activeTabId,
    switchToTab,
    closeTab,
    closePanel,
    closeCurrentContent,
    toggleCollapse,
    togglePosition,
    resizePanel,
    openFilePanel,
    openComponentTab,
  } = useSidePanel();

  const { addFloatingPanel } = useFloatingPanels();

  const {
    activeGroup,
    activeGroupId,
    createGroup,
    setActiveGroup,
    setAssignmentDoc,
    addReferenceDoc,
  } = useAssignment();

  // Handle sending messages from the Assignment Assistant
  const handleSendMessage = useCallback(
    (message) => {
      // This will be handled by the parent component (HomePage)
      window.dispatchEvent(
        new CustomEvent("sendChatMessage", {
          detail: {
            message: message,
            context: {
              assignmentId: activeGroupId,
              references: activeGroup?.referenceDocIds || [],
            },
          },
        })
      );
    },
    [activeGroup, activeGroupId]
  );
  const [key, setKey] = useState(0);
  const [objectUrl, setObjectUrl] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const panelRef = useRef(null);
  const dropdownRef = useRef(null);
  const contentRef = useRef(null);
  const iframeRef = useRef(null);

  // Selection state
  const [selectionText, setSelectionText] = useState("");
  const [selectionSource, setSelectionSource] = useState("none"); // 'dom' | 'pdf' | 'none'
  // Side-docked quick actions state
  const [showDock, setShowDock] = useState(false);
  const dockRef = useRef(null);
  const [dockTop, setDockTop] = useState(120); // px from top of content

  useEffect(() => {
    const panelActive = !isCollapsed && (panelFile || contentComponent);

    // Add classes to body to control main content layout
    document.body.classList.toggle("panel-open", panelActive);
    document.body.classList.toggle("panel-right", position === "right");
    document.body.classList.toggle("panel-left", position === "left");

    // Apply custom width to root element whenever panel is visible
    if (panelActive) {
      document.documentElement.style.setProperty("--panel-width", `${width}%`);
      document.documentElement.style.setProperty(
        "--content-width",
        `${100 - width}%`
      );
    }

    return () => {
      // Clean up classes and custom properties when component unmounts
      document.body.classList.remove("panel-open", "panel-right", "panel-left");
      document.documentElement.style.removeProperty("--panel-width");
      document.documentElement.style.removeProperty("--content-width");
    };
  }, [isCollapsed, position, panelFile, contentComponent, width]);

  // --- Assignment Group Helpers ---
  const findDocIdByName = useCallback(
    (name) => {
      if (!name) return null;
      const docs = availableDocuments || [];
      const match = docs.find((d) => (d.name || "").trim() === name.trim());
      return match ? match.id || null : null;
    },
    [availableDocuments]
  );

  const ensureActiveGroup = useCallback(() => {
    if (activeGroupId) return activeGroupId;
    const id = createGroup("Assignment Group");
    setActiveGroup(id);
    return id;
  }, [activeGroupId, createGroup, setActiveGroup]);

  const assignCurrentAsRole = useCallback(
    (role) => {
      if (!panelFile) return;
      const groupId = ensureActiveGroup();
      const docId = findDocIdByName(panelFile.name);
      if (!docId) return; // file might not be in storage yet
      if (role === "assignment") {
        setAssignmentDoc(groupId, docId);
      } else {
        addReferenceDoc(groupId, docId);
      }
    },
    [
      panelFile,
      ensureActiveGroup,
      findDocIdByName,
      setAssignmentDoc,
      addReferenceDoc,
    ]
  );

  useEffect(() => {
    if (panelFile) {
      const url = URL.createObjectURL(panelFile);
      setObjectUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [panelFile]);

  useEffect(() => {
    setKey((prev) => prev + 1);
  }, [position]);

  // Load available documents
  useEffect(() => {
    const loadDocuments = () => {
      const files = getFilesFromStorage();
      setAvailableDocuments(files);
    };

    loadDocuments();

    // Listen for file updates
    const handleFilesUpdated = () => {
      loadDocuments();
    };

    window.addEventListener("filesUpdated", handleFilesUpdated);
    return () => window.removeEventListener("filesUpdated", handleFilesUpdated);
  }, []);

  // State for pending quote highlight
  const [pendingQuoteHighlight, setPendingQuoteHighlight] = useState(null);

  // Handle opening document with quote highlight
  useEffect(() => {
    const handleOpenWithHighlight = (event) => {
      const { file, searchText, quote } = event.detail;

      if (!file) {
        console.error("No file provided for highlight");
        return;
      }

      // Store the quote to highlight
      setPendingQuoteHighlight({ searchText, quote });

      // Open the file in the panel
      openFilePanel(file);
    };

    window.addEventListener(
      "openDocumentWithHighlight",
      handleOpenWithHighlight
    );
    return () =>
      window.removeEventListener(
        "openDocumentWithHighlight",
        handleOpenWithHighlight
      );
  }, [openFilePanel]);

  // When we have a pending highlight, send message to PDF viewer iframe
  useEffect(() => {
    if (!pendingQuoteHighlight || !objectUrl) return;

    // Use the full search text for better matching
    const searchQuery = pendingQuoteHighlight.searchText;

    // Get page number if available
    const pageNum =
      parseInt(pendingQuoteHighlight.quote?.page_reference, 10) || 1;

    console.log(
      "Will search for:",
      searchQuery.substring(0, 50) + "...",
      "on page:",
      pageNum
    );

    // Function to send search message to iframe
    const sendSearchMessage = () => {
      const iframe = iframeRef.current;
      if (!iframe || !iframe.contentWindow) {
        console.log("Iframe not ready, retrying...");
        setTimeout(sendSearchMessage, 500);
        return;
      }

      console.log("Sending search message to PDF viewer");
      iframe.contentWindow.postMessage(
        {
          type: "pdf-search",
          query: searchQuery,
          page: pageNum,
          analysis: pendingQuoteHighlight.quote?.analysis || null,
        },
        "*"
      );

      setPendingQuoteHighlight(null);
    };

    // Wait for PDF to load before sending message
    const timer = setTimeout(sendSearchMessage, 2000);
    return () => clearTimeout(timer);
  }, [pendingQuoteHighlight, objectUrl]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        console.log("Click outside detected, closing dropdown");
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      // Add a small delay to prevent immediate closing from the same click that opened it
      const timer = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timer);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showDropdown]);

  const getCollapseIcon = () => {
    if (position === "right") {
      return isCollapsed ? (
        <ChevronLeft size={24} />
      ) : (
        <ChevronRight size={24} />
      );
    } else {
      return isCollapsed ? (
        <ChevronRight size={24} />
      ) : (
        <ChevronLeft size={24} />
      );
    }
  };

  // State for tracking resize dragging
  const [isDragging, setIsDragging] = useState(false);

  // Use refs to store the latest values for event handlers
  const isDraggingRef = useRef(false);
  const dragHandlersRef = useRef({
    move: null,
    end: null,
  });

  // Handle resize drag start
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    isDraggingRef.current = true;

    // Prevent text selection during drag
    document.body.style.userSelect = "none";

    // Add event listeners for drag move and end
    document.addEventListener("mousemove", dragHandlersRef.current.move);
    document.addEventListener("mouseup", dragHandlersRef.current.end);
    document.addEventListener("touchmove", dragHandlersRef.current.move);
    document.addEventListener("touchend", dragHandlersRef.current.end);
  }, []);

  // Handle resize drag move
  const handleDragMove = useCallback(
    (e) => {
      if (!isDraggingRef.current) return;

      // Get mouse/touch position
      const clientX = e.clientX || (e.touches && e.touches[0].clientX);
      if (!clientX) return;

      const windowWidth = window.innerWidth;
      let newWidthPercent;

      if (position === "right") {
        // For right-positioned panel, calculate from right edge of window
        newWidthPercent = ((windowWidth - clientX) / windowWidth) * 100;
      } else {
        // For left-positioned panel, calculate from left edge of window
        newWidthPercent = (clientX / windowWidth) * 100;
      }

      // Clamp width between 20% and 80%
      newWidthPercent = Math.max(20, Math.min(80, newWidthPercent));

      // Update panel width
      resizePanel(newWidthPercent);
    },
    [position, resizePanel]
  );

  // Handle resize drag end
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    isDraggingRef.current = false;

    // Remove event listeners
    document.removeEventListener("mousemove", dragHandlersRef.current.move);
    document.removeEventListener("mouseup", dragHandlersRef.current.end);
    document.removeEventListener("touchmove", dragHandlersRef.current.move);
    document.removeEventListener("touchend", dragHandlersRef.current.end);

    // Restore text selection
    document.body.style.userSelect = "";
  }, []);

  // Update refs when handlers change
  useEffect(() => {
    dragHandlersRef.current.move = handleDragMove;
    dragHandlersRef.current.end = handleDragEnd;
  }, [handleDragMove, handleDragEnd]);

  // Helper function to convert base64 to File
  const base64ToFile = (base64String, fileName, mimeType) => {
    const byteCharacters = atob(base64String);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new File([byteArray], fileName, { type: mimeType });
  };

  // Handle opening a document from dropdown
  const handleOpenDocument = (doc) => {
    try {
      console.log("Opening document from dropdown:", doc);

      // Check if document has content that needs conversion
      if (doc.content && doc.type) {
        const file = base64ToFile(doc.content, doc.name, doc.type);
        console.log("Converted file:", file);
        openFilePanel(file);
      } else if (doc.file) {
        // Use existing file object
        openFilePanel(doc.file);
      } else {
        console.error("Document has no valid content or file:", doc);
        return;
      }

      setShowDropdown(false);

      // Auto-close sidebar when opening a document
      // Dispatch custom event to notify HomePage to close sidebar
      window.dispatchEvent(new CustomEvent("closeSidebar"));
    } catch (error) {
      console.error("Error opening document from dropdown:", error);
    }
  };

  // Get all uploaded documents (not just unopened ones)
  const getAllDocuments = () => {
    if (!availableDocuments || availableDocuments.length === 0) {
      return [];
    }
    return availableDocuments;
  };

  // Utilities: selection handling for component content (not iframe)
  const clearSelectionToolbar = useCallback(() => {
    setSelectionText("");
    setSelectionSource("none");
    setShowDock(false);
  }, []);

  const updateSelectionToolbar = useCallback(() => {
    // Only handle selection within our own component wrapper
    const root = contentRef.current;
    if (!root) return clearSelectionToolbar();

    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.rangeCount)
      return clearSelectionToolbar();

    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    if (!rect || (rect.width === 0 && rect.height === 0))
      return clearSelectionToolbar();

    // Ensure selection is inside our contentRef
    const anchorNode =
      sel.anchorNode && sel.anchorNode.parentElement
        ? sel.anchorNode.parentElement
        : null;
    if (anchorNode && !root.contains(anchorNode)) {
      // Likely inside iframe or outside panel; ignore
      return clearSelectionToolbar();
    }

    const text = sel.toString().trim();
    if (!text) return clearSelectionToolbar();

    // Position dock near the first rect of the selection
    const clientRects = Array.from(range.getClientRects()).filter(
      (r) => r.width > 0 && r.height > 0
    );
    setSelectionText(text);
    setSelectionSource("dom");
    const panelRect = panelRef.current?.getBoundingClientRect();
    const first = clientRects[0];
    if (panelRect && first) {
      const top = Math.max(
        80,
        Math.min(first.top - panelRect.top, panelRect.height - 160)
      );
      setDockTop(top);
    }
    setShowDock(true);
  }, [clearSelectionToolbar]);

  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;

    let selectionTimer = null;
    const scheduleUpdate = (delay = 80) => {
      if (selectionTimer) clearTimeout(selectionTimer);
      selectionTimer = setTimeout(updateSelectionToolbar, delay);
    };

    const handleMouseDown = () => {
      if (selectionTimer) clearTimeout(selectionTimer);
      clearSelectionToolbar();
    };

    const handleMouseUp = () => {
      scheduleUpdate(80);
    };

    const handleKeyUp = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") {
        scheduleUpdate(0);
      } else if (e.key === "Escape") {
        clearSelectionToolbar();
      } else {
        scheduleUpdate(0);
      }
    };

    const handleScroll = () => clearSelectionToolbar();

    // No hover-gated show; dock shows on selection end and persists until dismissed/cleared
    const handleMouseMove = () => {};

    root.addEventListener("mousedown", handleMouseDown, true);
    root.addEventListener("mouseup", handleMouseUp, true);
    root.addEventListener("keyup", handleKeyUp);
    root.addEventListener("scroll", handleScroll, true);
    root.addEventListener("mousemove", handleMouseMove, true);

    return () => {
      if (selectionTimer) clearTimeout(selectionTimer);
      root.removeEventListener("mousedown", handleMouseDown, true);
      root.removeEventListener("mouseup", handleMouseUp, true);
      root.removeEventListener("keyup", handleKeyUp);
      root.removeEventListener("scroll", handleScroll, true);
      root.removeEventListener("mousemove", handleMouseMove, true);
    };
  }, [updateSelectionToolbar, clearSelectionToolbar]);

  // Bridge: receive selection events from PDF.js viewer inside iframe
  useEffect(() => {
    const onMessage = (e) => {
      const data = e && e.data;
      if (
        !data ||
        (data.type !== "pdf-selection" &&
          data.type !== "pdf-selection-clear" &&
          data.type !== "pdf-selection-hover" &&
          data.type !== "pdf-selection-hover-leave")
      )
        return;

      if (data.type === "pdf-selection-clear") {
        clearSelectionToolbar();
        return;
      }
      if (data.type === "pdf-selection") {
        const { text, firstRect, rect } = data;
        if (!text) return clearSelectionToolbar();
        setSelectionText(String(text).trim());
        setSelectionSource("pdf");
        // Position dock near PDF selection first rect
        const panelRect = panelRef.current?.getBoundingClientRect();
        const iframeRect = iframeRef.current?.getBoundingClientRect();
        const targetRect = firstRect || rect;
        if (panelRect && iframeRect && targetRect) {
          const absTop = iframeRect.top + targetRect.y;
          const top = Math.max(
            80,
            Math.min(absTop - panelRect.top, panelRect.height - 160)
          );
          setDockTop(top);
        }
        setShowDock(true);
        return;
      }

      if (data.type === "pdf-selection-hover") {
        // No longer needed to control visibility; keep for potential future fine-tuning
        return;
      }

      if (data.type === "pdf-selection-hover-leave") {
        // Do not auto-hide
        return;
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [clearSelectionToolbar, selectionSource, selectionText]);

  // Quick action handlers
  const handleCopy = useCallback(async () => {
    if (!selectionText) return;
    try {
      await navigator.clipboard.writeText(selectionText);
    } catch (e) {
      // no-op
    }
  }, [selectionText]);

  const dispatchChatEvent = useCallback(
    (action) => {
      if (!selectionText) return;
      window.dispatchEvent(
        new CustomEvent("sidepanelTextAction", {
          detail: { action, text: selectionText },
        })
      );
    },
    [selectionText]
  );

  const handleAsk = useCallback(
    () => dispatchChatEvent("ask"),
    [dispatchChatEvent]
  );
  const handleSummarize = useCallback(
    () => dispatchChatEvent("summarize"),
    [dispatchChatEvent]
  );

  // Handle file upload
  const handleFileUpload = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.doc,.docx,.txt,.md";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        // Convert to base64 and store
        const reader = new FileReader();
        reader.onload = () => {
          const base64Content = reader.result.split(",")[1];
          const fileData = {
            id: Date.now().toString(),
            name: file.name,
            type: file.type,
            content: base64Content,
            uploadDate: new Date().toISOString(),
          };

          // Add to storage
          const existingFiles = JSON.parse(
            localStorage.getItem("quizmate_files") || "[]"
          );
          existingFiles.push(fileData);
          localStorage.setItem("quizmate_files", JSON.stringify(existingFiles));

          // Notify other components
          window.dispatchEvent(new CustomEvent("filesUpdated"));

          // Open the uploaded file
          openFilePanel(file);
          setShowDropdown(false);
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  if (!panelFile && !contentComponent) return null;

  return (
    <div
      ref={panelRef}
      className={`${styles.sidePanel} ${styles[position]} ${
        isCollapsed ? styles.collapsed : ""
      }`}
      style={{ width: isCollapsed ? "" : `${width}%` }}
    >
      <div className={styles.header}>
        {position === "right" ? (
          <>
            <div className={styles.headerControls}>
              <button
                className={styles.positionButton}
                onClick={togglePosition}
                title="Move to left side"
              >
                <ArrowLeft size={24} />
              </button>
            </div>
            <h3>{panelTitle || panelFile?.name || ""}</h3>
            <button
              className={styles.closeButton}
              onClick={closePanel}
              title="Close panel"
            >
              <X size={24} />
            </button>
          </>
        ) : (
          <>
            <button
              className={styles.closeButton}
              onClick={closePanel}
              title="Close panel"
            >
              <X size={24} />
            </button>
            <h3>{panelTitle || panelFile?.name || ""}</h3>
            <button
              className={styles.positionButton}
              onClick={togglePosition}
              title="Move to right side"
            >
              <ArrowRight size={24} />
            </button>
          </>
        )}
      </div>

      {/* Tools quick access: Resources & Assignment */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "6px 10px",
          borderTop: "1px solid var(--border-color, #e5e7eb)",
        }}
      >
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => openComponentTab(<Resources />, "Resources")}
            title="Open Resources"
            style={{
              fontSize: 12,
              padding: "6px 8px",
              borderRadius: 6,
              border: "1px solid #ddd",
            }}
          >
            Resources
          </button>
        </div>
      </div>

      {/* Minimal role assignment controls */}
      {panelFile && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            padding: "6px 10px",
            borderTop: "1px solid var(--border-color, #e5e7eb)",
          }}
        >
          {activeGroup ? (
            <>
              <span style={{ fontSize: 12, color: "var(--muted, #6b7280)" }}>
                Active Group: <strong>{activeGroup.title}</strong>
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => assignCurrentAsRole("assignment")}
                  title="Set as Assignment"
                  style={{
                    fontSize: 12,
                    padding: "6px 8px",
                    borderRadius: 6,
                    border: "1px solid #ddd",
                  }}
                >
                  Set as Assignment
                </button>
                <button
                  onClick={() => assignCurrentAsRole("reference")}
                  title="Add to References"
                  style={{
                    fontSize: 12,
                    padding: "6px 8px",
                    borderRadius: 6,
                    border: "1px solid #ddd",
                  }}
                >
                  Add to References
                </button>
              </div>
            </>
          ) : (
            <button
              onClick={() => setActiveGroup(ensureActiveGroup())}
              title="Create and activate group"
              style={{
                fontSize: 12,
                padding: "6px 8px",
                borderRadius: 6,
                border: "1px solid #ddd",
              }}
            >
              Create Assignment Group
            </button>
          )}
        </div>
      )}

      <button
        className={`${styles.collapseButton} ${
          isCollapsed ? styles.collapsed : ""
        }`}
        onClick={toggleCollapse}
        title={isCollapsed ? "Expand panel" : "Collapse panel"}
      >
        <div className={styles.collapseIcon}>{getCollapseIcon()}</div>
      </button>

      {/* Resize edge */}
      <div
        className={`${styles.resizeEdge} ${styles[position]} ${
          isDragging ? styles.dragging : ""
        }`}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      ></div>

      {/* Browser-style tabs */}
      {documentTabs.length > 0 && (
        <div className={styles.tabsContainer}>
          <div className={styles.tabsList}>
            {documentTabs.map((tab) => (
              <div
                key={tab.id}
                className={`${styles.tab} ${
                  activeTabId === tab.id ? styles.activeTab : ""
                }`}
                onClick={() => switchToTab(tab.id)}
              >
                <span className={styles.tabName}>{tab.name}</span>
                <button
                  className={styles.tabPopOutButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Pop out tab to floating panel
                    const content =
                      tab.type === "component" ? tab.component : null;
                    if (content) {
                      // Use null to let the system auto-assign to next available quadrant
                      addFloatingPanel(content, tab.name, null);
                      closeTab(tab.id);
                    }
                  }}
                  title="Pop out to floating window"
                >
                  <ExternalLink size={12} />
                </button>
                <button
                  className={styles.tabCloseButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeTab(tab.id);
                  }}
                  title="Close tab"
                >
                  <X size={14} />
                </button>
              </div>
            ))}

            {/* Add new tab button with dropdown */}
            <div className={styles.addTabContainer} ref={dropdownRef}>
              <button
                className={styles.addTabButton}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log(
                    "Add tab button clicked - opening Resources tab as new tab"
                  );
                  // Open Resources as a new component tab
                  openComponentTab(<Resources />, "Resources");
                  // Close sidebar after opening resources
                  setTimeout(() => {
                    window.dispatchEvent(new CustomEvent("closeSidebar"));
                  }, 100);
                }}
                title="Open Resources tab"
              >
                <Plus size={14} />
              </button>

              {showDropdown && (
                <div className={styles.dropdown}>
                  <div className={styles.dropdownHeader}>
                    <span>Documents</span>
                  </div>
                  {console.log(
                    "Dropdown rendering, availableDocuments:",
                    availableDocuments,
                    "getAllDocuments():",
                    getAllDocuments()
                  )}

                  {/* Upload new document option */}
                  <div
                    className={`${styles.dropdownItem} ${styles.uploadOption}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleFileUpload();
                    }}
                  >
                    <Plus size={14} />
                    <span>Upload New Document</span>
                  </div>

                  {/* Separator */}
                  {getAllDocuments().length > 0 && (
                    <div className={styles.dropdownSeparator}></div>
                  )}

                  {/* Existing documents */}
                  {getAllDocuments().length > 0 ? (
                    getAllDocuments().map((doc) => (
                      <div
                        key={doc.id || doc.name}
                        className={styles.dropdownItem}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log("Dropdown item clicked:", doc);
                          handleOpenDocument(doc);
                        }}
                      >
                        <FileText size={14} />
                        <span>{doc.name || "Unnamed Document"}</span>
                      </div>
                    ))
                  ) : (
                    <div className={styles.dropdownItem}>
                      <span>No documents uploaded yet</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={styles.content} ref={contentRef}>
        {panelFile && (
          <iframe
            key={key}
            src={`/pdfjs/web/viewer.html?file=${encodeURIComponent(objectUrl)}`}
            className={styles.viewer}
            title="Document viewer"
            ref={iframeRef}
            style={{ pointerEvents: isDragging ? "none" : "auto" }}
          />
        )}
        {contentComponent && (
          <div className={styles.componentWrapper}>{contentComponent}</div>
        )}

        {/* Side-docked quick actions - hide when quote highlight is active */}
        {selectionText && !pendingQuoteHighlight && (
          <div
            ref={dockRef}
            className={`${styles.selectionDock} ${showDock ? styles.open : ""}`}
            style={{ top: dockTop }}
          >
            <div className={styles.selectionDockHeader}>
              <button
                className={styles.selectionDockToggle}
                title={showDock ? "Hide actions" : "Show actions"}
                onClick={() => setShowDock((prev) => !prev)}
              >
                {showDock ? "❯" : "❮"}
              </button>
            </div>
            <div className={styles.selectionDockBody}>
              <button
                className={styles.selectionBtn}
                onClick={handleCopy}
                title="Copy"
              >
                Copy
              </button>
              <button
                className={styles.selectionBtn}
                onClick={handleAsk}
                title="Ask Lumi"
              >
                Ask
              </button>
              <button
                className={styles.selectionBtn}
                onClick={handleSummarize}
                title="Summarize"
              >
                Summarize
              </button>
            </div>
          </div>
        )}

        {/* Re-open handle when dock is collapsed - hide when quote highlight is active */}
        {selectionText && !showDock && !pendingQuoteHighlight && (
          <button
            className={styles.selectionDockHandle}
            style={{ top: dockTop }}
            title="Show actions"
            onClick={() => setShowDock(true)}
          >
            ❮
          </button>
        )}
      </div>
    </div>
  );
};

export default SidePanel;
